import React, { useState, useEffect, useRef } from "react";
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import './Sitecontent.css';
import { gsap } from 'gsap';
import Loader from '../components/layout/preloader';


const Modal = ({ title, fields, data, onSave, onClose }) => {
  const [form, setForm] = useState(data || {});
  const modalRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (modalRef.current) {
      gsap.fromTo(
        modalRef.current,
        { opacity: 0, scale: 0.94, y: 20 },
        { opacity: 1, scale: 1, y: 0, duration: 0.32, ease: 'power3.out' }
      );
    }
  }, []);

  const handleClose = () => {
    if (modalRef.current) {
      gsap.to(modalRef.current, {
        opacity: 0,
        scale: 0.94,
        y: 14,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: onClose,
      });
    } else {
      onClose();
    }
  };

  return (
    <div className="sc-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="sc-modal" ref={modalRef}>
        <div className="sc-modal-header">
          <h2 className="sc-modal-title">{title}</h2>
          <button className="sc-modal-close" onClick={handleClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="sc-modal-body">
          {fields.map((f) => (
            <div className="sc-field" key={f.key}>
              <label className="sc-label">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea
                  className={`sc-input sc-textarea${f.dir === "rtl" ? " rtl" : ""}`}
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={3}
                />
              ) : f.type === "checkbox" ? (
                <input
                  type="checkbox"
                  className="sc-checkbox"
                  checked={!!form[f.key]}
                  onChange={(e) => set(f.key, e.target.checked)}
                />
              ) : (
                <input
                  type="text"
                  className={`sc-input${f.dir === "rtl" ? " rtl" : ""}`}
                  value={form[f.key] || ""}
                  onChange={(e) => set(f.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
        <div className="sc-modal-footer">
          <button className="sc-btn-cancel" onClick={handleClose}>Cancel</button>
          <button className="sc-btn-save" onClick={() => onSave(form)}>Save Changes</button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ id, label, children, animRef }) => (
  <section className="sc-section" id={id} ref={animRef}>
    <div className="sc-section-header">
      <span className="sc-section-label">{label}</span>
    </div>
    {children}
  </section>
);

const RowCard = ({ children, onEdit, onDelete }) => (
  <div className="sc-row-card">
    <div className="sc-row-content">{children}</div>
    <div className="sc-row-actions">
      <button className="sc-icon-btn sc-edit-btn" title="Edit" onClick={onEdit}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
      <button className="sc-icon-btn sc-del-btn" title="Delete" onClick={onDelete}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6l-1 14H6L5 6"/>
          <path d="M10 11v6M14 11v6"/>
          <path d="M9 6V4h6v2"/>
        </svg>
      </button>
    </div>
  </div>
);

const FieldPair = ({ enLabel, arLabel, enVal, arVal }) => (
  <div className="sc-field-pair">
    <div className="sc-field-item">
      <span className="sc-field-label">{enLabel}</span>
      <span className="sc-field-val">{enVal || <em className="sc-empty">—</em>}</span>
    </div>
    <div className="sc-field-item rtl">
      <span className="sc-field-label">{arLabel}</span>
      <span className="sc-field-val sc-rtl">{arVal || <em className="sc-empty">—</em>}</span>
    </div>
  </div>
);

const AddBtn = ({ onClick, label = "Add" }) => (
  <button className="sc-add-btn" onClick={onClick}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
    {label}
  </button>
);

const SiteContent = () => {
  const [modal, setModal] = useState(null);
  const [hero, setHero] = useState([]);
  const [aboutContent, setAboutContent] = useState([]);
  const [aboutEventStatic, setAboutEventStatic] = useState([]);
  const [aboutStats, setAboutStats] = useState([]);
  const [aboutCta, setAboutCta] = useState([]);
  const [designTalks, setDesignTalks] = useState([]);
  const [detailsAbout, setDetailsAbout] = useState([]);
  const [detailsPoints, setDetailsPoints] = useState([]);
  const [detailsExpect, setDetailsExpect] = useState([]);
  const [bookingPolicies, setBookingPolicies] = useState([]);
  const [ticketCtaPoints, setTicketCtaPoints] = useState([]);
  const [ticketCtaStatic, setTicketCtaStatic] = useState([]);
  const [faqSection, setFaqSection] = useState([]);
  const [loading, setLoading] = useState(true);

  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);

  const fetchAll = async () => {
    setLoading(true);
    const fetches = await Promise.all([
      supabase.from("landing_page_hero").select("*"),
      supabase.from("about_section_content").select("*"),
      supabase.from("about_event_static").select("*"),
      supabase.from("about_section_stats").select("*").order("display_order"),
      supabase.from("about_section_cta").select("*"),
      supabase.from("design_talks").select("*").order("created_at"),
      supabase.from("details_about").select("*"),
      supabase.from("details_about_points").select("*").order("display_order"),
      supabase.from("details_expectations").select("*"),
      supabase.from("booking_policies").select("*").order("display_order"),
      supabase.from("ticketing_cta_points").select("*"),
      supabase.from("ticketing_cta_static").select("*"),
      supabase.from("faq_section").select("*").order("display_order"),
    ]);
    const [h, ac, ae, as_, acta, dt, da, dp, de, bp, tcp, tcs, faq] = fetches.map((r) => r.data || []);
    setHero(h); setAboutContent(ac); setAboutEventStatic(ae); setAboutStats(as_);
    setAboutCta(acta); setDesignTalks(dt); setDetailsAbout(da); setDetailsPoints(dp);
    setDetailsExpect(de); setBookingPolicies(bp); setTicketCtaPoints(tcp);
    setTicketCtaStatic(tcs); setFaqSection(faq);
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (loading) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 4 });

      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: -22 },
        { opacity: 1, y: 0, duration: 0.6 }
      );

      tl.fromTo(
        [section1Ref.current, section2Ref.current, section3Ref.current],
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.55, stagger: 0.14 },
        '-=0.3'
      );

      const rowCards = rootRef.current?.querySelectorAll('.sc-row-card');
      if (rowCards && rowCards.length) {
        tl.fromTo(
          rowCards,
          { opacity: 0, x: -16 },
          { opacity: 1, x: 0, duration: 0.35, stagger: 0.04, ease: 'power2.out' },
          '-=0.3'
        );
      }

      const subHeaders = rootRef.current?.querySelectorAll('.sc-subsection-header');
      if (subHeaders && subHeaders.length) {
        gsap.fromTo(
          subHeaders,
          { opacity: 0 },
          { opacity: 1, duration: 0.5, stagger: 0.08, ease: 'power2.out', delay: 4.4 }
        );
      }
    }, rootRef);

    return () => ctx.revert();
  }, [loading]);

  const handleSave = async (table, item, isNew) => {
    let res;
    if (isNew) {
      res = await supabase.from(table).insert([item]);
    } else {
      const { id, ...rest } = item;
      res = await supabase.from(table).update(rest).eq("id", id);
    }
    if (!res.error) {
      setModal(null);
      fetchAll();
    }
  };

  const handleDelete = async (table, id) => {
    await supabase.from(table).delete().eq("id", id);
    fetchAll();
  };

  const openEdit = (title, fields, data, table) =>
    setModal({ title, fields, data, table, isNew: false });

  const openAdd = (title, fields, table, defaults = {}) =>
    setModal({ title, fields, data: defaults, table, isNew: true });

  const heroFields = [
    { key: "title_en", label: "Title (EN)" },
    { key: "title_ar", label: "Title (AR)", dir: "rtl" },
    { key: "subtitle_en", label: "Subtitle (EN)" },
    { key: "subtitle_ar", label: "Subtitle (AR)", dir: "rtl" },
    { key: "description_en", label: "Description (EN)", type: "textarea" },
    { key: "description_ar", label: "Description (AR)", type: "textarea", dir: "rtl" },
    { key: "img_h", label: "Image URL" },
  ];

  const aboutContentFields = [
    { key: "main_title_en", label: "Main Title (EN)" },
    { key: "main_title_ar", label: "Main Title (AR)", dir: "rtl" },
    { key: "para_one_en", label: "Paragraph 1 (EN)", type: "textarea" },
    { key: "para_one_ar", label: "Paragraph 1 (AR)", type: "textarea", dir: "rtl" },
    { key: "para_two_en", label: "Paragraph 2 (EN)", type: "textarea" },
    { key: "para_two_ar", label: "Paragraph 2 (AR)", type: "textarea", dir: "rtl" },
    { key: "para_three_en", label: "Paragraph 3 (EN)", type: "textarea" },
    { key: "para_three_ar", label: "Paragraph 3 (AR)", type: "textarea", dir: "rtl" },
  ];

  const aboutEventStaticFields = [
    { key: "tag_label_en", label: "Tag Label (EN)" },
    { key: "tag_label_ar", label: "Tag Label (AR)", dir: "rtl" },
    { key: "headline_en", label: "Headline (EN)" },
    { key: "headline_ar", label: "Headline (AR)", dir: "rtl" },
    { key: "description_en", label: "Description (EN)", type: "textarea" },
    { key: "description_ar", label: "Description (AR)", type: "textarea", dir: "rtl" },
    { key: "read_more_cta_en", label: "Read More CTA (EN)" },
    { key: "read_more_cta_ar", label: "Read More CTA (AR)", dir: "rtl" },
  ];

  const statsFields = [
    { key: "stat_value", label: "Stat Value (e.g. 100+)" },
    { key: "stat_label_en", label: "Label (EN)" },
    { key: "stat_label_ar", label: "Label (AR)", dir: "rtl" },
    { key: "display_order", label: "Display Order" },
  ];

  const ctaFields = [
    { key: "title_en", label: "Title (EN)" },
    { key: "title_ar", label: "Title (AR)", dir: "rtl" },
    { key: "content_en", label: "Content (EN)", type: "textarea" },
    { key: "content_ar", label: "Content (AR)", type: "textarea", dir: "rtl" },
    { key: "button_text_en", label: "Button Text (EN)" },
    { key: "button_text_ar", label: "Button Text (AR)", dir: "rtl" },
    { key: "is_active", label: "Is Active", type: "checkbox" },
  ];

  const talksFields = [
    { key: "talk_time_en", label: "Time (EN)" },
    { key: "talk_time_ar", label: "Time (AR)", dir: "rtl" },
    { key: "speaker_name_en", label: "Speaker (EN)" },
    { key: "speaker_name_ar", label: "Speaker (AR)", dir: "rtl" },
    { key: "topic_en", label: "Topic (EN)" },
    { key: "topic_ar", label: "Topic (AR)", dir: "rtl" },
    { key: "img", label: "Image URL" },
  ];

  const detailsAboutFields = [
    { key: "title_en", label: "Title (EN)" },
    { key: "title_ar", label: "Title (AR)", dir: "rtl" },
    { key: "content_en", label: "Content (EN)", type: "textarea" },
    { key: "content_ar", label: "Content (AR)", type: "textarea", dir: "rtl" },
    { key: "philosophy_title_en", label: "Philosophy Title (EN)" },
    { key: "philosophy_title_ar", label: "Philosophy Title (AR)", dir: "rtl" },
    { key: "philosophy_content_en", label: "Philosophy Content (EN)", type: "textarea" },
    { key: "philosophy_content_ar", label: "Philosophy Content (AR)", type: "textarea", dir: "rtl" },
  ];

  const detailsPointsFields = [
    { key: "point_en", label: "Point (EN)" },
    { key: "point_ar", label: "Point (AR)", dir: "rtl" },
    { key: "display_order", label: "Display Order" },
  ];

  const detailsExpectFields = [
    { key: "title_en", label: "Title (EN)" },
    { key: "title_ar", label: "Title (AR)", dir: "rtl" },
    { key: "content_en", label: "Content (EN)", type: "textarea" },
    { key: "content_ar", label: "Content (AR)", type: "textarea", dir: "rtl" },
    { key: "booth_label_en", label: "Booth Label (EN)" },
    { key: "booth_label_ar", label: "Booth Label (AR)", dir: "rtl" },
    { key: "booth_value", label: "Booth Value" },
    { key: "location_label_en", label: "Location Label (EN)" },
    { key: "location_label_ar", label: "Location Label (AR)", dir: "rtl" },
    { key: "location_value_en", label: "Location Value (EN)" },
    { key: "location_value_ar", label: "Location Value (AR)", dir: "rtl" },
    { key: "cta_text_en", label: "CTA Text (EN)" },
    { key: "cta_text_ar", label: "CTA Text (AR)", dir: "rtl" },
  ];

  const policiesFields = [
    { key: "section_title_en", label: "Section Title (EN)" },
    { key: "section_title_ar", label: "Section Title (AR)", dir: "rtl" },
    { key: "point_text_en", label: "Point Text (EN)", type: "textarea" },
    { key: "point_text_ar", label: "Point Text (AR)", type: "textarea", dir: "rtl" },
    { key: "display_order", label: "Display Order" },
    { key: "is_highlight", label: "Is Highlight", type: "checkbox" },
  ];

  const ticketCtaPointsFields = [
    { key: "point_title_en", label: "Point Title (EN)" },
    { key: "point_title_ar", label: "Point Title (AR)", dir: "rtl" },
    { key: "point_desc_en", label: "Point Description (EN)", type: "textarea" },
    { key: "point_desc_ar", label: "Point Description (AR)", type: "textarea", dir: "rtl" },
  ];

  const ticketCtaStaticFields = [
    { key: "main_tag_en", label: "Main Tag (EN)" },
    { key: "main_tag_ar", label: "Main Tag (AR)", dir: "rtl" },
    { key: "headline_en", label: "Headline (EN)" },
    { key: "headline_ar", label: "Headline (AR)", dir: "rtl" },
    { key: "description_en", label: "Description (EN)", type: "textarea" },
    { key: "description_ar", label: "Description (AR)", type: "textarea", dir: "rtl" },
    { key: "book_ticket_cta_en", label: "Book Ticket CTA (EN)" },
    { key: "book_ticket_cta_ar", label: "Book Ticket CTA (AR)", dir: "rtl" },
    { key: "view_exhibitors_cta_en", label: "View Exhibitors CTA (EN)" },
    { key: "view_exhibitors_cta_ar", label: "View Exhibitors CTA (AR)", dir: "rtl" },
    { key: "ticket_front_img", label: "Ticket Front Image URL" },
  ];

  const faqFields = [
    { key: "question_en", label: "Question (EN)" },
    { key: "question_ar", label: "Question (AR)", dir: "rtl" },
    { key: "answer_en", label: "Answer (EN)", type: "textarea" },
    { key: "answer_ar", label: "Answer (AR)", type: "textarea", dir: "rtl" },
    { key: "display_order", label: "Display Order" },
    { key: "is_active", label: "Is Active", type: "checkbox" },
  ];

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard/site-content">
        <div className="sc-root" ref={rootRef}>
          <div className="sc-page-header" ref={headerRef}>
            <div>
              <h1 className="sc-page-title">SITE CONTENT</h1>
              <p className="sc-page-sub">Manage all website content — bilingual (EN / AR)</p>
            </div>
          </div>

          <>
            <Section id="homepage" label="HOMEPAGE" animRef={section1Ref}>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Hero</span>
                  <AddBtn onClick={() => openAdd("Add Hero Record", heroFields, "landing_page_hero")} label="Add Hero" />
                </div>
                {hero.length === 0 && <p className="sc-empty-state">No hero records found.</p>}
                {hero.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Hero Record", heroFields, row, "landing_page_hero")}
                    onDelete={() => handleDelete("landing_page_hero", row.id)}
                  >
                    <FieldPair enLabel="Title (EN)" arLabel="العنوان (AR)" enVal={row.title_en} arVal={row.title_ar} />
                    <FieldPair enLabel="Subtitle (EN)" arLabel="العنوان الفرعي (AR)" enVal={row.subtitle_en} arVal={row.subtitle_ar} />
                    <FieldPair enLabel="Description (EN)" arLabel="الوصف (AR)" enVal={row.description_en} arVal={row.description_ar} />
                    {row.img_h && <div className="sc-img-thumb"><img src={row.img_h} alt="hero" /></div>}
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">About Event — Static Block</span>
                  <AddBtn onClick={() => openAdd("Add Event Static", aboutEventStaticFields, "about_event_static")} label="Add Block" />
                </div>
                {aboutEventStatic.length === 0 && <p className="sc-empty-state">No records.</p>}
                {aboutEventStatic.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Event Static", aboutEventStaticFields, row, "about_event_static")}
                    onDelete={() => handleDelete("about_event_static", row.id)}
                  >
                    <FieldPair enLabel="Tag Label (EN)" arLabel="التصنيف" enVal={row.tag_label_en} arVal={row.tag_label_ar} />
                    <FieldPair enLabel="Headline (EN)" arLabel="العنوان الرئيسي" enVal={row.headline_en} arVal={row.headline_ar} />
                    <FieldPair enLabel="Description (EN)" arLabel="الوصف" enVal={row.description_en} arVal={row.description_ar} />
                    <FieldPair enLabel="Read More CTA (EN)" arLabel="زر اقرأ المزيد" enVal={row.read_more_cta_en} arVal={row.read_more_cta_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Design Talks</span>
                  <AddBtn onClick={() => openAdd("Add Design Talk", talksFields, "design_talks")} label="Add Talk" />
                </div>
                {designTalks.length === 0 && <p className="sc-empty-state">No records.</p>}
                {designTalks.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Design Talk", talksFields, row, "design_talks")}
                    onDelete={() => handleDelete("design_talks", row.id)}
                  >
                    <FieldPair enLabel="Time (EN)" arLabel="الوقت" enVal={row.talk_time_en} arVal={row.talk_time_ar} />
                    <FieldPair enLabel="Speaker (EN)" arLabel="المتحدث" enVal={row.speaker_name_en} arVal={row.speaker_name_ar} />
                    <FieldPair enLabel="Topic (EN)" arLabel="الموضوع" enVal={row.topic_en} arVal={row.topic_ar} />
                    {row.img && <div className="sc-img-thumb"><img src={row.img} alt="speaker" /></div>}
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Ticketing CTA — Static</span>
                  <AddBtn onClick={() => openAdd("Add Ticketing CTA Static", ticketCtaStaticFields, "ticketing_cta_static")} label="Add CTA Block" />
                </div>
                {ticketCtaStatic.length === 0 && <p className="sc-empty-state">No records.</p>}
                {ticketCtaStatic.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Ticketing CTA Static", ticketCtaStaticFields, row, "ticketing_cta_static")}
                    onDelete={() => handleDelete("ticketing_cta_static", row.id)}
                  >
                    <FieldPair enLabel="Main Tag (EN)" arLabel="التصنيف الرئيسي" enVal={row.main_tag_en} arVal={row.main_tag_ar} />
                    <FieldPair enLabel="Headline (EN)" arLabel="العنوان" enVal={row.headline_en} arVal={row.headline_ar} />
                    <FieldPair enLabel="Description (EN)" arLabel="الوصف" enVal={row.description_en} arVal={row.description_ar} />
                    <FieldPair enLabel="Book Ticket CTA (EN)" arLabel="زر الحجز" enVal={row.book_ticket_cta_en} arVal={row.book_ticket_cta_ar} />
                    <FieldPair enLabel="View Exhibitors CTA (EN)" arLabel="زر العارضين" enVal={row.view_exhibitors_cta_en} arVal={row.view_exhibitors_cta_ar} />
                    {row.ticket_front_img && <div className="sc-img-thumb"><img src={row.ticket_front_img} alt="ticket" /></div>}
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Ticketing CTA — Feature Points</span>
                  <AddBtn onClick={() => openAdd("Add Ticketing Point", ticketCtaPointsFields, "ticketing_cta_points")} label="Add Point" />
                </div>
                {ticketCtaPoints.length === 0 && <p className="sc-empty-state">No records.</p>}
                {ticketCtaPoints.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Ticketing Point", ticketCtaPointsFields, row, "ticketing_cta_points")}
                    onDelete={() => handleDelete("ticketing_cta_points", row.id)}
                  >
                    <FieldPair enLabel="Title (EN)" arLabel="العنوان" enVal={row.point_title_en} arVal={row.point_title_ar} />
                    <FieldPair enLabel="Description (EN)" arLabel="الوصف" enVal={row.point_desc_en} arVal={row.point_desc_ar} />
                  </RowCard>
                ))}
              </div>

            </Section>

            <Section id="about" label="ABOUT PAGE" animRef={section2Ref}>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">About Section Content</span>
                  <AddBtn onClick={() => openAdd("Add About Content", aboutContentFields, "about_section_content")} label="Add Content" />
                </div>
                {aboutContent.length === 0 && <p className="sc-empty-state">No records.</p>}
                {aboutContent.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit About Content", aboutContentFields, row, "about_section_content")}
                    onDelete={() => handleDelete("about_section_content", row.id)}
                  >
                    <FieldPair enLabel="Main Title (EN)" arLabel="العنوان الرئيسي" enVal={row.main_title_en} arVal={row.main_title_ar} />
                    <FieldPair enLabel="Paragraph 1 (EN)" arLabel="الفقرة الأولى" enVal={row.para_one_en} arVal={row.para_one_ar} />
                    <FieldPair enLabel="Paragraph 2 (EN)" arLabel="الفقرة الثانية" enVal={row.para_two_en} arVal={row.para_two_ar} />
                    <FieldPair enLabel="Paragraph 3 (EN)" arLabel="الفقرة الثالثة" enVal={row.para_three_en} arVal={row.para_three_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Statistics</span>
                  <AddBtn onClick={() => openAdd("Add Stat", statsFields, "about_section_stats", { display_order: aboutStats.length + 1 })} label="Add Stat" />
                </div>
                {aboutStats.length === 0 && <p className="sc-empty-state">No records.</p>}
                {aboutStats.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Stat", statsFields, row, "about_section_stats")}
                    onDelete={() => handleDelete("about_section_stats", row.id)}
                  >
                    <FieldPair enLabel="Value" arLabel="القيمة" enVal={row.stat_value} arVal={row.stat_value} />
                    <FieldPair enLabel="Label (EN)" arLabel="التسمية" enVal={row.stat_label_en} arVal={row.stat_label_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">About CTA Block</span>
                  <AddBtn onClick={() => openAdd("Add CTA", ctaFields, "about_section_cta", { is_active: true })} label="Add CTA" />
                </div>
                {aboutCta.length === 0 && <p className="sc-empty-state">No records.</p>}
                {aboutCta.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit CTA", ctaFields, row, "about_section_cta")}
                    onDelete={() => handleDelete("about_section_cta", row.id)}
                  >
                    <FieldPair enLabel="Title (EN)" arLabel="العنوان" enVal={row.title_en} arVal={row.title_ar} />
                    <FieldPair enLabel="Content (EN)" arLabel="المحتوى" enVal={row.content_en} arVal={row.content_ar} />
                    <FieldPair enLabel="Button (EN)" arLabel="زر" enVal={row.button_text_en} arVal={row.button_text_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Details — About</span>
                  <AddBtn onClick={() => openAdd("Add Details About", detailsAboutFields, "details_about")} label="Add Detail" />
                </div>
                {detailsAbout.length === 0 && <p className="sc-empty-state">No records.</p>}
                {detailsAbout.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Details About", detailsAboutFields, row, "details_about")}
                    onDelete={() => handleDelete("details_about", row.id)}
                  >
                    <FieldPair enLabel="Title (EN)" arLabel="العنوان" enVal={row.title_en} arVal={row.title_ar} />
                    <FieldPair enLabel="Content (EN)" arLabel="المحتوى" enVal={row.content_en} arVal={row.content_ar} />
                    <FieldPair enLabel="Philosophy Title (EN)" arLabel="فلسفة التصميم" enVal={row.philosophy_title_en} arVal={row.philosophy_title_ar} />
                    <FieldPair enLabel="Philosophy Content (EN)" arLabel="محتوى الفلسفة" enVal={row.philosophy_content_en} arVal={row.philosophy_content_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Details — Achievement Points</span>
                  <AddBtn onClick={() => openAdd("Add Point", detailsPointsFields, "details_about_points", { display_order: detailsPoints.length + 1 })} label="Add Point" />
                </div>
                {detailsPoints.length === 0 && <p className="sc-empty-state">No records.</p>}
                {detailsPoints.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Point", detailsPointsFields, row, "details_about_points")}
                    onDelete={() => handleDelete("details_about_points", row.id)}
                  >
                    <FieldPair enLabel="Point (EN)" arLabel="النقطة" enVal={row.point_en} arVal={row.point_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">FAQ</span>
                  <AddBtn onClick={() => openAdd("Add FAQ", faqFields, "faq_section", { is_active: true, display_order: faqSection.length + 1 })} label="Add FAQ" />
                </div>
                {faqSection.length === 0 && <p className="sc-empty-state">No questions found.</p>}
                {faqSection.map((row, i) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit(`Edit FAQ #${row.display_order || i + 1}`, faqFields, row, "faq_section")}
                    onDelete={() => handleDelete("faq_section", row.id)}
                  >
                    <div className="sc-faq-num">FAQ {row.display_order || i + 1}</div>
                    <FieldPair enLabel="Question (EN)" arLabel="السؤال" enVal={row.question_en} arVal={row.question_ar} />
                    <FieldPair enLabel="Answer (EN)" arLabel="الإجابة" enVal={row.answer_en} arVal={row.answer_ar} />
                  </RowCard>
                ))}
              </div>

            </Section>

            <Section id="booking" label="BOOKING PAGE" animRef={section3Ref}>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">Booking Policies</span>
                  <AddBtn onClick={() => openAdd("Add Policy", policiesFields, "booking_policies", { is_highlight: true, display_order: bookingPolicies.length + 1 })} label="Add Policy" />
                </div>
                {bookingPolicies.length === 0 && <p className="sc-empty-state">No records.</p>}
                {bookingPolicies.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Policy", policiesFields, row, "booking_policies")}
                    onDelete={() => handleDelete("booking_policies", row.id)}
                  >
                    <FieldPair enLabel="Section Title (EN)" arLabel="عنوان القسم" enVal={row.section_title_en} arVal={row.section_title_ar} />
                    <FieldPair enLabel="Point Text (EN)" arLabel="نص النقطة" enVal={row.point_text_en} arVal={row.point_text_ar} />
                  </RowCard>
                ))}
              </div>

              <div className="sc-subsection">
                <div className="sc-subsection-header">
                  <span className="sc-sub-label">What to Expect</span>
                  <AddBtn onClick={() => openAdd("Add Expectation", detailsExpectFields, "details_expectations")} label="Add Expectation" />
                </div>
                {detailsExpect.length === 0 && <p className="sc-empty-state">No records.</p>}
                {detailsExpect.map((row) => (
                  <RowCard key={row.id}
                    onEdit={() => openEdit("Edit Expectation", detailsExpectFields, row, "details_expectations")}
                    onDelete={() => handleDelete("details_expectations", row.id)}
                  >
                    <FieldPair enLabel="Title (EN)" arLabel="العنوان" enVal={row.title_en} arVal={row.title_ar} />
                    <FieldPair enLabel="Content (EN)" arLabel="المحتوى" enVal={row.content_en} arVal={row.content_ar} />
                    <FieldPair enLabel={`Booth: ${row.booth_label_en}`} arLabel={row.booth_label_ar} enVal={row.booth_value} arVal={row.booth_value} />
                    <FieldPair enLabel={`Location: ${row.location_label_en}`} arLabel={row.location_label_ar} enVal={row.location_value_en} arVal={row.location_value_ar} />
                    <FieldPair enLabel="CTA (EN)" arLabel="زر الإجراء" enVal={row.cta_text_en} arVal={row.cta_text_ar} />
                  </RowCard>
                ))}
              </div>

            </Section>
          </>
        </div>

        {modal && (
          <Modal
            title={modal.title}
            fields={modal.fields}
            data={modal.data}
            onSave={(form) => handleSave(modal.table, form, modal.isNew)}
            onClose={() => setModal(null)}
          />
        )}
      </DashboardLayout>
    </>
  );
};

export default SiteContent;